import { Component, OnInit, AfterViewInit, signal, computed, TemplateRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import moment from 'moment';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { DataTableComponent } from 'src/app/@shared/components/data-table/data-table.component';
import { TableColumn } from 'src/app/@shared/models/table.models';
import { SelectButtonComponent } from 'src/app/@core/components/ui/select-button/select-button.component';
import { SelectOption } from 'src/app/@core/components/ui/select/select.model';
import { DatepickerComponent } from 'src/app/@core/components/ui/datepicker/datepicker.component';
import { DatePickerConfig } from 'src/app/@core/components/ui/datepicker/datepicker.model';

import { CREDITS_SVG } from 'src/app/common/svgs';
import { CreditsOverviewComponent } from 'src/app/features/credits/components/credits-overview/credits-overview.component';
import { MenuComponent, CustomMenuItem } from 'src/app/@core/components/ui/menu/menu.component';
import { UserService } from 'src/app/contract-management/services/user.service';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { IUser } from 'src/app/@core/models/model';
import { USER_ROLE, ADDITIONAL_ROLES } from 'src/app/@core/models/enums';

import {
    UserCreditsData, UserCreditsService, UsageRecord,
    OrgAnalyticsData, UsageTrendPoint, CreditAlertUser,
} from 'src/app/features/credits/services/user-credits.service';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { CheckoutItem, CreditsCheckoutDialogComponent } from 'src/app/features/credits/components/credits-checkout-dialog/credits-checkout-dialog.component';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { NgxEchartsModule, NGX_ECHARTS_CONFIG } from 'ngx-echarts';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { TabsPanelsComponent } from 'src/app/@core/components/ui/tabs-panels/tabs-panels.component';
import { CHECKOUT_SESSION_TYPE } from 'src/app/contract-management/services/payment.service';

export enum CREDIT_TAB { USER = 'user', ORG = 'org' }

type DateRangePreset = 'cycle' | '30d' | '90d' | 'custom';

@Component({
    selector: 'app-credit-usage',
    standalone: true,
    imports: [
        CommonModule, FormsModule, DataTableComponent, SelectButtonComponent,
        CreditsOverviewComponent, MenuComponent, IconComponent,
        ButtonComponent, DatePipe, CreditsCheckoutDialogComponent, UserNameDisplayComponent,
        NgxEchartsModule, TabsPanelsComponent, UserNameDisplayComponent, DatepickerComponent
    ],
    providers: [
        {
            provide: NGX_ECHARTS_CONFIG,
            useValue: { echarts },
        },
    ],
    templateUrl: './credit-usage.component.html',
    styleUrl: './credit-usage.component.scss',
})
export class CreditUsageComponent implements OnInit, AfterViewInit {

    currentUser: IUser | undefined;
    readonly CREDITS_SVG = CREDITS_SVG;
    readonly CREDIT_TAB = CREDIT_TAB;

    activeTab: CREDIT_TAB = CREDIT_TAB.USER;

    CHECKOUT_SESSION_TYPE = CHECKOUT_SESSION_TYPE;

    get creditTabs() {
        const tabs = [{ value: CREDIT_TAB.USER, label: 'User' }];
        if (this.canViewOrgTab) tabs.push({ value: CREDIT_TAB.ORG, label: 'Organization' });
        return tabs;
    }

    onTabChange(event: { value: string }) {
        this.activeTab = event.value as CREDIT_TAB;
        if (this.activeTab === CREDIT_TAB.ORG && this.canViewOrgTab) {
            this.fetchOrgAnalytics();
        }
        this.refreshTrend();
    }

    @ViewChild('creditsOverviewRef') creditsOverviewRef: CreditsOverviewComponent | undefined;
    @ViewChild('dateCellRef') dateCellRef!: TemplateRef<any>;
    @ViewChild('modeCellRef') modeCellRef!: TemplateRef<any>;
    @ViewChild('featureCellRef') featureCellRef!: TemplateRef<any>;
    @ViewChild('detailsCellRef') detailsCellRef!: TemplateRef<any>;
    @ViewChild('alertUserCellRef') alertUserCellRef!: TemplateRef<any>;
    @ViewChild('alertModeCellRef') alertModeCellRef!: TemplateRef<any>;
    @ViewChild('alertUsageCellRef') alertUsageCellRef!: TemplateRef<any>;

    constructor(
        private userService: UserService,
        private authService: AuthenticationService,
        private userCreditsService: UserCreditsService,
        private cdr: ChangeDetectorRef,
    ) { }

    ngAfterViewInit() {
        this.customTemplatesMap = {
            'dateCell': this.dateCellRef,
            'modeCell': this.modeCellRef,
            'featureCell': this.featureCellRef,
            'detailsCell': this.detailsCellRef,
        };
        this.alertTemplatesMap = {
            'alertUserCell': this.alertUserCellRef,
            'alertModeCell': this.alertModeCellRef,
            'alertUsageCell': this.alertUsageCellRef,
        };
        this.cdr.detectChanges();
    }

    customTemplatesMap: { [key: string]: TemplateRef<any> } = {};

    alertColumns: TableColumn[] = [
        { field: 'user', header: 'User', type: 'custom', template: 'alertUserCell' },
        { field: 'type', header: 'Mode', type: 'custom', template: 'alertModeCell', width: '100px' },
        { field: 'consumed', header: 'Usage', type: 'custom', template: 'alertUsageCell' },
    ];

    alertTemplatesMap: { [key: string]: TemplateRef<any> } = {};

    get isSuperAdmin(): boolean {
        return this.currentUser?.role === USER_ROLE.SUPER_ADMIN;
    }

    get isAdminOrSuperAdmin(): boolean {
        return this.currentUser?.role === USER_ROLE.SUPER_ADMIN || this.currentUser?.role === USER_ROLE.ADMIN;
    }

    get isBillingAdmin(): boolean {
        return this.currentUser?.additionalRoles?.includes(ADDITIONAL_ROLES.BILLING_ADMIN) ?? false;
    }

    /** True for account owner (SUPER_ADMIN) and org admin (ADMIN) only. MEMBER is excluded even with BILLING_ADMIN role. */
    get canViewOrgTab(): boolean {
        return this.isAdminOrSuperAdmin;
    }

    get currentUserAskMode(): boolean {
        return this.currentUser?.agentModeSettings?.askMode ?? true;
    }

    get currentUserAgentMode(): boolean {
        return this.currentUser?.agentModeSettings?.agentMode ?? true;
    }

    get canGetCredits(): boolean {
        return this.currentUserAskMode || this.currentUserAgentMode;
    }

    get canAddCredits(): boolean {
        return this.isSuperAdmin &&
            !!this.selectedUserOption?.data &&
            this.selectedUserOption.data._id !== this.currentUser?._id;
    }


    selectedPreset: DateRangePreset = 'cycle';

    presetOptions: SelectOption[] = [
        { label: 'Current Cycle', value: 'cycle' },
        { label: 'Last 30 Days', value: '30d' },
        { label: 'Last 90 Days', value: '90d' },
        { label: 'Custom Range', value: 'custom' },
    ];

    customDateRange: Date | Date[] | null = null;

    get dateRange(): { start: Date | null; end: Date | null } {
        const now = new Date();
        if (this.selectedPreset === '30d') {
            const start = new Date(now); start.setDate(now.getDate() - 30);
            return { start, end: now };
        }
        if (this.selectedPreset === '90d') {
            const start = new Date(now); start.setDate(now.getDate() - 90);
            return { start, end: now };
        }
        if (this.selectedPreset === 'custom' && Array.isArray(this.customDateRange) && this.customDateRange.length === 2) {
            return { start: this.customDateRange[0], end: this.customDateRange[1] };
        }
        return { start: null, end: null };
    }

    onPresetChange(event: any) {
        this.selectedPreset = event.value as DateRangePreset;
        if (this.selectedPreset !== 'custom') this.refreshTrend();
    }

    onCustomRangeChange() {
        if (Array.isArray(this.customDateRange) && this.customDateRange.length === 2 && this.customDateRange[1]) {
            this.refreshTrend();
        }
    }


    orgSelectedUser = 'All';
    orgSelectedMode: 'All' | 'ask' | 'agent' = 'All';

    orgModeMenuItems: CustomMenuItem[] = [
        { label: 'All Modes', value: 'All' },
        { label: 'Ask', value: 'ask' },
        { label: 'Agent', value: 'agent' },
    ];
    selectedOrgModeData: CustomMenuItem[] = [];

    orgUserMenuItems: CustomMenuItem[] = [];
    selectedOrgUserData: CustomMenuItem[] = [];

    get selectedOrgModeLabel(): string {
        return this.orgModeMenuItems.find(i => i.value === this.orgSelectedMode)?.label ?? 'All Modes';
    }

    get selectedOrgUserOption(): CustomMenuItem | undefined {
        return this.orgUserMenuItems.find(i => i.value === this.orgSelectedUser);
    }

    onOrgModeSelected(item: CustomMenuItem) {
        this.orgSelectedMode = item.value as 'All' | 'ask' | 'agent';
        this.selectedOrgModeData = [item];
        this.onOrgFilterChange();
    }

    onOrgUserSelected(item: CustomMenuItem) {
        this.orgSelectedUser = item.value;
        this.selectedOrgUserData = [item];
        this.onOrgFilterChange();
    }

    onOrgFilterChange() {
        if (this.activeTab === CREDIT_TAB.ORG) {
            this.fetchOrgAnalytics();
        }
        this.refreshTrend();
    }


    userCredits: UserCreditsData | null = null;
    usageData: UsageRecord[] = [];
    userMenuItems: CustomMenuItem[] = [];
    selectedUserMenuData: CustomMenuItem[] = [];
    selectedUser = 'All';

    get selectedUserOption(): CustomMenuItem | undefined {
        return this.userMenuItems.find(i => i.value === this.selectedUser);
    }

    onUserSelected(item: CustomMenuItem) {
        this.selectedUser = item.value;
        this.selectedUserMenuData = [item];
        if (item.data?._id) this.fetchUserCredits(item.data._id);
        this.refreshTrend();
    }

    fetchUserCredits(userId: string) {
        this.userCreditsService.getCreditsByUser(userId).subscribe((res: any) => {
            const data: UserCreditsData = res?.data || res;
            this.userCredits = data;
            // `feature` and `details` are resolved server-side; no client-side mapping needed.
            this.usageData = data?.usage || [];
        });
    }


    filterOptions = [
        { label: 'All', value: 'All' },
        { label: 'Ask', value: 'ask' },
        { label: 'Agent', value: 'agent' },
    ];
    selectedFilter: 'All' | 'ask' | 'agent' = 'All';

    get filteredData() {
        if (this.selectedFilter === 'All') return this.usageData;
        return this.usageData.filter(item =>
            item.creditType?.toLowerCase() === this.selectedFilter.toLowerCase()
        );
    }

    onFilterChange() {
        this.refreshTrend();
    }


    columns: TableColumn[] = [
        { field: 'title', header: 'Details', type: 'custom', template: 'detailsCell' },
        { field: 'createdAt', header: 'Date', type: 'custom', template: 'dateCell' },
        { field: 'creditType', header: 'Mode', type: 'custom', template: 'modeCell' },
        { field: 'feature', header: 'Feature', type: 'custom', template: 'featureCell' },
        { field: 'totalTokens', header: 'Total Tokens', type: 'number' },
        { field: 'consumedCredits', header: 'Credits Used', type: 'number' },
    ];


    checkoutDialogVisible = false;
    checkoutItems: CheckoutItem[] = [];
    readonly checkoutSuccessUrl = '/settings/credit-usage?session_id={CHECKOUT_SESSION_ID}&credit_type=TOP_UP&success=true';
    readonly checkoutCancelUrl = '/settings/credit-usage';

    openAddCreditsDialog() {
        this.checkoutItems = this.userMenuItems
            .filter(item => item.data?._id)
            .map(item => {
                const user = item.data as IUser;
                return {
                    requestId: user._id,
                    request: {
                        _id: user._id,
                        name: user.fullName || `${user.firstName} ${user.lastName}`,
                        email: user.email,
                        requestedBy: user,
                    } as any,
                    askCredits: 0,
                    agentCredits: 0,
                };
            });
        this.checkoutDialogVisible = true;
    }


    orgAnalytics = signal<OrgAnalyticsData | null>(null);

    get orgAsk() { return this.orgAnalytics()?.creditSummary.find(s => s._id === 'ASK'); }
    get orgAgent() { return this.orgAnalytics()?.creditSummary.find(s => s._id === 'AGENT'); }

    get orgTotalConsumed(): number {
        return (this.orgAsk?.totalConsumed ?? 0) + (this.orgAgent?.totalConsumed ?? 0);
    }

    get orgTotalAllocated(): number {
        return (this.orgAsk?.totalAllocated ?? 0) + (this.orgAgent?.totalAllocated ?? 0);
    }

    get orgUtilizationPct(): number {
        if (!this.orgTotalAllocated) return 0;
        return Math.min(100, Math.round((this.orgTotalConsumed / this.orgTotalAllocated) * 100));
    }

    get orgAskPct(): number {
        const ask = this.orgAsk;
        if (!ask?.totalAllocated) return 0;
        return Math.min(100, Math.round((ask.totalConsumed / ask.totalAllocated) * 100));
    }

    get orgAgentPct(): number {
        const agent = this.orgAgent;
        if (!agent?.totalAllocated) return 0;
        return Math.min(100, Math.round((agent.totalConsumed / agent.totalAllocated) * 100));
    }

    get orgSeatsPct(): number {
        const seats = this.orgAnalytics()?.seats;
        if (!seats?.total) return 0;
        return Math.round((seats.withCredits / seats.total) * 100);
    }

    orgOverviewCards = computed(() => {
        const analytics = this.orgAnalytics();
        if (!analytics) return [];

        const totalConsumed = this.orgTotalConsumed;
        const totalAllocated = this.orgTotalAllocated;
        const utilizationPct = this.orgUtilizationPct;
        const ask = this.orgAsk;
        const askPct = this.orgAskPct;
        const agent = this.orgAgent;
        const agentPct = this.orgAgentPct;
        const seatsPct = this.orgSeatsPct;

        return [
            {
                id: 'total-consumed',
                title: 'Total Consumed',
                value: totalConsumed,
                percentage: utilizationPct,
                subtitle: `of ${totalAllocated.toFixed(0)} allocated`,
                borderColor: 'bg-slate-400',
                valueColor: 'text-surface-900',
                percentColor: 'text-surface-500',
                barColor: 'bg-slate-400',
            },
            {
                id: 'utilization',
                title: 'Utilization',
                value: utilizationPct,
                percentage: utilizationPct,
                subtitle: 'of total capacity',
                suffix: '%',
                borderColor: utilizationPct <= 75 ? 'bg-emerald-500' : utilizationPct <= 90 ? 'bg-amber-400' : 'bg-red-500',
                valueColor: utilizationPct <= 75 ? 'text-emerald-600' : utilizationPct <= 90 ? 'text-amber-500' : 'text-red-500',
                percentColor: utilizationPct <= 75 ? 'text-emerald-600' : utilizationPct <= 90 ? 'text-amber-500' : 'text-red-500',
                barColor: utilizationPct <= 75 ? 'bg-emerald-500' : utilizationPct <= 90 ? 'bg-amber-400' : 'bg-red-500',
            },
            {
                id: 'ask-credits',
                title: 'Ask Credits',
                value: ask?.totalConsumed ?? 0,
                percentage: askPct,
                subtitle: `of ${ask?.totalAllocated?.toFixed(0) ?? 0}`,
                borderColor: 'bg-arena-orange',
                valueColor: 'text-arena-orange',
                percentColor: 'text-arena-orange',
                barColor: 'bg-arena-orange',
            },
            {
                id: 'agent-credits',
                title: 'Agent Credits',
                value: agent?.totalConsumed ?? 0,
                percentage: agentPct,
                subtitle: `of ${agent?.totalAllocated?.toFixed(0) ?? 0}`,
                borderColor: 'bg-arena-blue',
                valueColor: 'text-arena-blue',
                percentColor: 'text-arena-blue',
                barColor: 'bg-arena-blue',
            },
            {
                id: 'active-users',
                title: 'Active Users',
                value: analytics.seats?.withCredits ?? 0,
                secondaryValue: analytics.seats?.total ?? 0,
                percentage: seatsPct,
                subtitle: 'with credits assigned',
                borderColor: 'bg-violet-500',
                valueColor: 'text-surface-900',
                percentColor: 'text-violet-600',
                barColor: 'bg-violet-500',
            },
        ];
    });

    fetchOrgAnalytics() {
        this.userCreditsService.getOrgAnalytics().subscribe((res: any) => {
            this.orgAnalytics.set(res?.data || res);
        });
    }


    get atLimitUsers(): CreditAlertUser[] { return this.orgAnalytics()?.limitAlerts.atLimit ?? []; }
    get blockedUsers(): CreditAlertUser[] { return this.orgAnalytics()?.limitAlerts.blocked ?? []; }
    get negativeBalanceUsers(): CreditAlertUser[] { return this.orgAnalytics()?.limitAlerts.negativeBalance ?? []; }

    readonly allAlertRows = computed(() => {
        const analytics = this.orgAnalytics();
        if (!analytics) return [];
        return [
            ...(analytics.limitAlerts.negativeBalance ?? []).map(u => ({ ...u, status: 'negative' as const })),
            ...(analytics.limitAlerts.blocked ?? []).map(u => ({ ...u, status: 'blocked' as const })),
            ...(analytics.limitAlerts.atLimit ?? []).map(u => ({ ...u, status: 'near-limit' as const })),
        ];
    });

    get hasAlerts(): boolean {
        return this.allAlertRows().length > 0;
    }



    trendData = signal<UsageTrendPoint[]>([]);
    trendLoading = signal(false);

    trendPresetValue: '7d' | '30d' | '90d' | null = '7d';
    trendPresetOptions = [
        { label: '7d', value: '7d' },
        { label: '30d', value: '30d' },
        { label: '90d', value: '90d' },
    ];

    trendCustomDateRange: Date | Date[] | null = null;

    readonly trendDatepickerConfig: DatePickerConfig = {
        selectionMode: 'range',
        placeholder: 'Select a date range',
        maxDate: new Date(),
        appendTo: 'body',
        showButtonBar: false,
        inputStyle: { width: '13rem' },
    };

    onTrendPresetChange(value: '7d' | '30d' | '90d') {
        this.trendPresetValue = value;
        this.trendCustomDateRange = null;
        this.refreshTrend();
    }

    onTrendDateRangeSelect(value: Date | Date[] | null) {
        if (Array.isArray(value) && value.length === 2 && value[1]) {
            this.trendPresetValue = null;
            this.refreshTrend();
        }
    }


    trendChartOption = computed<EChartsOption>(() => {
        const points = this.trendData();
        if (!points.length) return {};

        const dates = points.map(p =>
            `${p._id.year}-${String(p._id.month).padStart(2, '0')}-${String(p._id.day).padStart(2, '0')}`
        );

        const fmtDate = (val: string) => 
            moment(val, 'YYYY-MM-DD').format('MMM D');

        const fmtNum = (v: number) =>
            v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(+v.toFixed(1));

        return {
            animation: true,
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#1e1e2e',
                borderColor: 'transparent',
                textStyle: { color: '#e2e8f0', fontSize: 12 },
                padding: [10, 14],
            },
            legend: {
                top: '8px',
                right: '50px',
                textStyle: { color: '#94a3b8', fontSize: 11 },
                backgroundColor: 'transparent',
            },
            grid: { left: 52, right: 16, top: 40, bottom: 16 },
            xAxis: {
                type: 'category',
                data: dates,
                boundaryGap: false,
                axisLine: { lineStyle: { color: '#e2e8f0' } },
                axisTick: { show: false },
                axisLabel: {
                    formatter: fmtDate,
                    color: '#94a3b8',
                    fontSize: 11,
                    hideOverlap: true,
                    interval: Math.max(0, Math.floor(dates.length / 8) - 1),
                },
                splitLine: { show: false },
            },
            yAxis: {
                type: 'value',
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    formatter: fmtNum,
                    color: '#94a3b8',
                    fontSize: 11,
                },
                splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
            },
            series: [
                {
                    name: 'Ask',
                    type: 'line',
                    smooth: 0.4,
                    symbolSize: 0,
                    data: points.map(p => +(p.askCredits ?? 0).toFixed(2)),
                    lineStyle: { color: '#f97316', width: 2 },
                    itemStyle: { color: '#f97316' },
                    areaStyle: {
                        color: {
                            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                            colorStops: [
                                { offset: 0, color: 'rgba(249,115,22,0.35)' },
                                { offset: 1, color: 'rgba(249,115,22,0.02)' },
                            ],
                        } as any,
                    },
                    emphasis: { focus: 'series' },
                },
                {
                    name: 'Agent',
                    type: 'line',
                    smooth: 0.4,
                    symbolSize: 0,
                    data: points.map(p => +(p.agentCredits ?? 0).toFixed(2)),
                    lineStyle: { color: '#6366f1', width: 2 },
                    itemStyle: { color: '#6366f1' },
                    areaStyle: {
                        color: {
                            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                            colorStops: [
                                { offset: 0, color: 'rgba(99,102,241,0.35)' },
                                { offset: 1, color: 'rgba(99,102,241,0.02)' },
                            ],
                        } as any,
                    },
                    emphasis: { focus: 'series' },
                },
            ],
        };
    });

    refreshTrend() {
        const now = new Date();
        let startDate: string;
        let endDate: string;

        if (this.trendPresetValue === null) {
            const dates = this.trendCustomDateRange;
            if (!Array.isArray(dates) || dates.length < 2 || !dates[1]) return;
            startDate = moment(dates[0]).format('YYYY-MM-DD');
            endDate = moment(dates[1]).format('YYYY-MM-DD');
        } else {
            const start = new Date(now);
            if (this.trendPresetValue === '7d') start.setDate(now.getDate() - 7);
            else if (this.trendPresetValue === '30d') start.setDate(now.getDate() - 30);
            else start.setDate(now.getDate() - 90);
            startDate = moment(start).format('YYYY-MM-DD');
            endDate = moment(now).format('YYYY-MM-DD');
        }

        const params: Record<string, string> = {
            startDate,
            endDate,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        if (this.activeTab === CREDIT_TAB.USER) {
            const selUser = this.selectedUserOption;
            const isAllUsers = this.isSuperAdmin && !selUser?.data?._id;
            if (!isAllUsers && selUser?.data?._id) params['userId'] = selUser.data._id;

            if (this.selectedFilter !== 'All') params['type'] = this.selectedFilter;
        } else if (this.activeTab === CREDIT_TAB.ORG) {
            if (this.orgSelectedUser !== 'All') params['userId'] = this.orgSelectedUser;
            if (this.orgSelectedMode !== 'All') params['type'] = this.orgSelectedMode;
        }

        this.trendLoading.set(true);
        this.userCreditsService.getUsageTrend(params).subscribe({
            next: (res: any) => {
                this.trendData.set(res?.data || res || []);
                this.trendLoading.set(false);
            },
            error: () => this.trendLoading.set(false),
        });
    }


    ngOnInit() {
        this.currentUser = this.authService.getUser() ?? undefined;
        this.fetchUsers();
        if (this.canViewOrgTab) this.fetchOrgAnalytics();
        this.refreshTrend();
    }

    fetchUsers() {
        this.userService.getUsers().subscribe(res => {
            if (res?.data?.users) {
                const users = res.data.users;
                const options: CustomMenuItem[] = [];
                const orgMenuItems: CustomMenuItem[] = [{ label: 'All Users', value: 'All' }];

                users.forEach(user => {
                    const isCurrentUser = this.currentUser && this.currentUser._id === user._id;
                    const label = isCurrentUser ? `${user.fullName} (You)` : user.fullName;
                    options.push({ label: label ?? '', value: user.fullName ?? '', description: user.email ?? '', data: user });
                    orgMenuItems.push({ label: label ?? '', value: user._id ?? '', description: user.email ?? '', data: user });
                });

                this.userMenuItems = options;
                this.orgUserMenuItems = orgMenuItems;

                const currentUserData = options.find(o => o.data?._id === this.currentUser?._id);
                if (currentUserData) {
                    this.selectedUser = currentUserData.value;
                    this.selectedUserMenuData = [currentUserData];
                    if (currentUserData.data?._id) this.fetchUserCredits(currentUserData.data._id);
                }
            }
        });
    }
}
